/**
 * Building a module — the creator's side of a playlist.
 *
 * Two screens' worth of work in one, because they are one thought:
 *
 *  1. **What the module is.** A title, a subject and a level. This comes first
 *     and it comes *before* any capsule is chosen, because it is the answer that
 *     decides which capsules may be chosen: a module is one subject at one level,
 *     and asking for the capsules first would mean offering a creator work from
 *     their whole library and then refusing most of it. The moment the three are
 *     given, the record is written — a draft with an empty playlist — so that
 *     everything after this point has an address to hang off.
 *
 *  2. **What is in it.** Six places, drawn as six places whether or not they are
 *     filled. This is the shape the feature was asked for and it is the honest
 *     one: a creator opening an empty module sees what a module *is* — six
 *     capsules, this subject, this level — rather than an empty list and a button
 *     whose consequences they have to imagine. Each empty place is a way to fill
 *     it, so the shortest path through the whole flow is: name it, tap an empty
 *     place, choose six capsules. No separate "create" step, no second screen.
 *
 * ## The one rule that shapes everything
 *
 * **A capsule written inside a module is an ordinary capsule.** The studio opened
 * from a place in the playlist is the capsule studio, with the module's subject
 * and level preselected and its own record handed back to the module on publish
 * (see `#/screens/CapsuleStudio`). Nothing about writing a capsule changes, and
 * the module only ever *gathers* — which is why this screen has no capsule editor
 * in it, and why the picker offers drafts as well as published work. The six
 * places are therefore not "requirements to be satisfied" but a list, some of
 * whose entries may be work the creator still has to finish.
 *
 * ## Drafts, saving and the gate
 *
 * The record exists from step one and is saved after every edit, so a module is a
 * thing that can be left and come back to — and the title, the playlist and the
 * order are all on it. The *gate* is a publish-time question: six **published**
 * capsules at that subject and level, which `modulePublishBlockers` answers and
 * the publish dialog enforces. It is deliberately not enforced while building:
 * a creator assembling a course across a week must be able to hold drafts in it.
 */
import {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {ScrollView, StyleSheet, View} from 'react-native'
import type Animated from 'react-native-reanimated'
import {useAnimatedRef, useScrollViewOffset} from 'react-native-reanimated'
import {Image} from 'expo-image'
import {AppBskyEmbedVideo} from '@atproto/api'
import {Plural, Trans, useLingui} from '@lingui/react/macro'
import {useIsFocused} from '@react-navigation/native'

import {
  CAPSULE_CATEGORIES,
  CAPSULE_GRADES,
  categoryOfLevel,
  getAvailableSubjects,
  gradeLabel,
  subjectLabel,
} from '#/lib/curriculum'
import {
  appendModuleItem,
  moveModuleItem,
  removeModuleItem,
} from '#/lib/moduleItems'
import {
  MODULE_MIN_CAPSULES,
  moduleCoinCount,
  moduleFilingSuggestion,
  type ModuleItem,
  modulePublishBlockers,
  moduleReadyCount,
  moduleSlots,
} from '#/lib/modulePlaylist'
import {moduleUriFromPath} from '#/lib/moduleRecord'
import {moduleRecordOf, writeModuleRecord} from '#/lib/moduleSave'
import {
  type CommonNavigatorParams,
  type NativeStackScreenProps,
} from '#/lib/routes/types'
import {useA11y} from '#/state/a11y'
import {useActorCapsulesQuery} from '#/state/queries/capsule'
import {
  useClassroomPostViews,
  usePostViewsByUri,
} from '#/state/queries/classroom-posts'
import {
  useAddClassroomContentMutation,
  useClassroomQuery,
} from '#/state/queries/classrooms'
import {useModuleByUriQuery} from '#/state/queries/module'
import {useAgent, useSession} from '#/state/session'
import * as Toast from '#/view/com/util/Toast'
import {showClassroomError} from '#/screens/Classroom/components/showClassroomError'
import {atoms as a, useBreakpoints, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {useDialogControl} from '#/components/Dialog'
import {SortableList} from '#/components/DraggableList'
import * as TextField from '#/components/forms/TextField'
import {
  ArrowBottom_Stroke2_Corner0_Rounded as ArrowBottom,
  ArrowTop_Stroke2_Corner0_Rounded as ArrowTop,
} from '#/components/icons/Arrow'
import {ChevronRight_Stroke2_Corner0_Rounded as ChevronRight} from '#/components/icons/Chevron'
import {CircleCheck_Stroke2_Corner0_Rounded as CircleCheck} from '#/components/icons/CircleCheck'
import {Pencil_Stroke2_Corner0_Rounded as Pencil} from '#/components/icons/Pencil'
import {PlusLarge_Stroke2_Corner0_Rounded as Plus} from '#/components/icons/Plus'
import {Trash_Stroke2_Corner0_Rounded as Trash} from '#/components/icons/Trash'
import * as Layout from '#/components/Layout'
import {Loader} from '#/components/Loader'
import {moduleBlockerSentence} from '#/components/modules/ModuleBlockerText'
import {ModuleCapsulePicker} from '#/components/modules/ModuleCapsulePicker'
import {
  type PublishedModule,
  PublishModuleDialog,
} from '#/components/modules/PublishModuleDialog'
import {Text} from '#/components/Typography'
import {IS_DEV} from '#/env'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'ModuleStudio'>

/**
 * How tall one place in the playlist is drawn.
 *
 * Dragging makes every row the same height, because a row's position *is*
 * arithmetic — `index × stride` — rather than a measurement it can take for
 * itself. So the place is a fixed 68 with a 4px gap under it, and its title is
 * one line: a row that grew with its words would be a row the list could not
 * place. The words are on the capsule's own page; here the title is a label.
 */
const MODULE_ROW_HEIGHT = 68
const MODULE_ROW_GAP = 4
const MODULE_ROW_STRIDE = MODULE_ROW_HEIGHT + MODULE_ROW_GAP

/**
 * A place in the playlist is identified by its capsule, and nothing else.
 *
 * The order *is* the list, so the position is never part of what a place is —
 * a key of `0`, `1`, `2` would tell React that every row after a move is a
 * different row, and the whole list would re-mount on every drag. The uri is
 * what a place actually is, and it is also what the record stores.
 */
const moduleItemKey = (item: ModuleItem) => item.uri

export function ModuleStudioScreen({route, navigation}: Props) {
  /* The address off a URL is percent-encoded; decoded once, here. */
  const draftUri = moduleUriFromPath(route.params?.draftUri)
  const classroomId = route.params?.classroomId
  const fixture = IS_DEV ? route.params?.fixture : undefined

  const t = useTheme()
  const {t: l, i18n} = useLingui()
  const {gtMobile} = useBreakpoints()
  const agent = useAgent()
  const {currentAccount} = useSession()
  const did = currentAccount?.did

  const {data: classroomRoom} = useClassroomQuery(classroomId)
  const classroom = classroomRoom?.classroom
  // The server says whether this account may put work in the room.
  const forClassroom = !!classroomRoom?.viewer.canPost
  const addClassroomContent = useAddClassroomContentMutation(
    classroom?.id ?? '',
  )

  /**
   * The room's own year, when the room has one the records can carry.
   *
   * A module built inside a classroom is the room's course, so it is filed at the
   * room's level: the school and year steps open on the room's answers and are
   * not offered as chips at all (see the filing block below). A room with no
   * level, or a level the record vocabulary has no years for, leaves the steps
   * exactly as they were.
   */
  const roomLevel =
    classroom?.grade && categoryOfLevel(classroom.grade)
      ? classroom.grade
      : undefined

  const draftQuery = useModuleByUriQuery(draftUri)
  const {data: myCapsules, isPending: capsulesPending} =
    useActorCapsulesQuery(did)

  const pickerControl = useDialogControl()
  const publishControl = useDialogControl()

  const [moduleUri, setModuleUri] = useState<string | undefined>(draftUri)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [level, setLevel] = useState<string | null>(roomLevel ?? null)
  const [subject, setSubject] = useState<string | null>(null)
  const [items, setItems] = useState<ModuleItem[]>([])
  const [createdAt, setCreatedAt] = useState<string>(() =>
    new Date().toISOString(),
  )
  const [status, setStatus] = useState<'draft' | 'published'>('draft')

  /** Which family of years the creator is picking from, in the first step. */
  const [category, setCategory] = useState<string | null>(() =>
    categoryOfLevel(roomLevel),
  )
  const [busy, setBusy] = useState(false)
  const [saving, setSaving] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)
  /** Whether the filing below was guessed for them, so the screen can say so. */
  const [filingGuessed, setFilingGuessed] = useState(false)

  /*
   * The playlist is dragged, so the screen has to be told to stop scrolling.
   *
   * A drag and a scroll are the same gesture as far as the platform is
   * concerned, and the one that wins is the one that was started first — which
   * on a scrolling page means the drag loses and the list slides out from under
   * the finger. `SavedFeeds` answers this the same way: the list reports when a
   * drag begins and the content container stops scrolling for its duration.
   */
  const {screenReaderEnabled} = useA11y()
  const [isDragging, setIsDragging] = useState(false)
  const scrollRef = useAnimatedRef<Animated.ScrollView>()
  const scrollOffset = useScrollViewOffset(scrollRef)

  /**
   * The record as this screen last saw it.
   *
   * The studio holds the playlist in its own state because every edit is local
   * until it is written, and the record is written after each one — which means
   * a refetch can arrive carrying the creator's *own* last write. Adopting it
   * would be harmless, and adopting one mid-typing would not, so the stamp of the
   * version this screen last read (or wrote) is remembered and a record that
   * matches it is left alone.
   */
  const syncedRef = useRef<string | null>(null)
  const stampOf = (record?: {
    updatedAt?: string
    createdAt?: string
    items: ModuleItem[]
  }) =>
    record
      ? `${record.updatedAt ?? record.createdAt ?? ''}|${record.items.length}`
      : null

  useEffect(() => {
    const record = draftQuery.data
    if (!record) return
    const stamp = stampOf(record)
    if (syncedRef.current === stamp) return
    const first = syncedRef.current === null
    syncedRef.current = stamp
    if (first) {
      setTitle(record.title)
      setDescription(record.description)
      setLevel(record.level ?? null)
      setSubject(record.subject ?? null)
      setCreatedAt(record.createdAt || new Date().toISOString())
      setStatus(record.status)
    }
    setItems(record.items)
  }, [draftQuery.data])

  /*
   * Coming back from a capsule written inside the module.
   *
   * The capsule studio appends the new capsule to the module's own record — it
   * cannot hand it back through the navigation stack, because a module is a
   * record that outlives one device — so the playlist this screen is holding is
   * stale the moment it becomes visible again. Refetching on focus is what reads
   * the append back, and the stamp above is what keeps that refetch from
   * overwriting an edit made here in the meantime.
   */
  const isFocused = useIsFocused()
  const {refetch} = draftQuery
  useEffect(() => {
    if (isFocused && moduleUri && draftUri) void refetch()
  }, [draftUri, isFocused, moduleUri, refetch])

  /**
   * The test fixture's module: named and filed, with nothing in it.
   *
   * It seeds the *identity* only, and never writes anything — a fixture that
   * wrote a record would leave one behind on the tester's own repo. The six
   * places below are then drawn from the author's real capsules, which is what
   * makes the builder measurable without inventing work that does not exist.
   */
  const seededRef = useRef(false)
  useEffect(() => {
    if (!fixture || seededRef.current) return
    seededRef.current = true
    setTitle('Fixture module')
    /*
     * The family too, not just the level.
     *
     * The first step draws the Year chips only once a School has been chosen, so a
     * fixture that seeded the level alone threw the builder into its second half
     * while the step above it still looked untouched — the year was set and had no
     * row to say so.
     */
    setCategory(categoryOfLevel('Level_A5'))
    setLevel('Level_A5')
    setSubject('Maths')
  }, [fixture])

  const capsules = useMemo(() => myCapsules ?? [], [myCapsules])
  const slots = useMemo(
    () => moduleSlots({items, capsules, level, subject}),
    [capsules, items, level, subject],
  )
  /** A place by its capsule, so a render does not scan the playlist per row. */
  const slotByUri = useMemo(
    () => new Map(slots.map(slot => [slot.item.uri, slot])),
    [slots],
  )

  /*
   * The pictures.
   *
   * A capsule's film lives on its *post*, not on its record (see `#/lib/capsuleCover`
   * for the one exception), so a playlist drawn from records has to ask the posts
   * for their stills or it renders six tiles with no covers. One batched request
   * for the whole playlist.
   */
  const postUris = useMemo(
    () =>
      items
        .map(
          item =>
            item.postUri ??
            capsules.find(capsule => capsule.uri === item.uri)?.postUri,
        )
        .filter((uri): uri is string => !!uri),
    [capsules, items],
  )
  const {data: postViews} = useClassroomPostViews(postUris)
  const viewsByUri = usePostViewsByUri(postViews)
  const thumbnailFor = useCallback(
    (item: ModuleItem): string | undefined => {
      const postUri =
        item.postUri ??
        capsules.find(capsule => capsule.uri === item.uri)?.postUri
      if (!postUri) return undefined
      const embed = viewsByUri.get(postUri)?.embed
      return embed && AppBskyEmbedVideo.isView(embed)
        ? embed.thumbnail
        : undefined
    },
    [capsules, viewsByUri],
  )
  const ready = moduleReadyCount(slots)
  const coins = useMemo(
    () =>
      moduleCoinCount(
        items
          .map(item => capsules.find(capsule => capsule.uri === item.uri))
          .filter(
            (capsule): capsule is NonNullable<typeof capsule> => !!capsule,
          ),
      ),
    [capsules, items],
  )
  const blockers = useMemo(
    () => modulePublishBlockers({title, level, subject, items, capsules}),
    [capsules, items, level, subject, title],
  )

  /**
   * Writing the record.
   *
   * One function for every edit, because every edit is the same write: the whole
   * record, with the thing that changed changed. A PDS keeps one version of a
   * record, so a partial update is not a thing that exists — and writing the
   * whole thing is what keeps the order, the title and the level from being three
   * separate saves that can land out of order.
   */
  const persist = useCallback(
    async (next: {
      items?: ModuleItem[]
      title?: string
      description?: string
      level?: string | null
      subject?: string | null
    }) => {
      if (!did || !moduleUri || fixture) return
      const now = new Date().toISOString()
      const nextItems = next.items ?? items
      setSaving(true)
      try {
        await writeModuleRecord({
          agent,
          did,
          moduleUri,
          record: moduleRecordOf({
            title: next.title ?? title,
            description: next.description ?? description,
            status,
            items: nextItems,
            level: next.level ?? level,
            subject: next.subject ?? subject,
            createdAt,
            updatedAt: now,
          }),
        })
        /*
         * The record this screen now knows about is the one it just wrote, so the
         * stamp is set to match rather than left to a refetch: without this, the
         * next focus would treat the app's own write as somebody else's change.
         *
         * `status` is not part of the stamp, so the length and the timestamp are
         * what it is built from — which is exactly the pair the write changed.
         */
        syncedRef.current = `${now}|${nextItems.length}`
      } catch (e) {
        /*
         * A save that failed is worth saying out loud, because the screen looks
         * exactly the same either way — the playlist is in React state and the
         * record is what the next device will see.
         */
        console.error('Failed to save module', e)
        Toast.show(l`Could not save the module`)
        return false
      } finally {
        setSaving(false)
      }
      return true
    },
    [
      agent,
      createdAt,
      description,
      did,
      fixture,
      items,
      level,
      moduleUri,
      status,
      subject,
      title,
    ],
  )

  /**
   * Every playlist edit goes through here: change the state, then write it.
   *
   * The state is set first so the screen answers the tap immediately, and the
   * write carries the *new* list explicitly rather than reading the state back —
   * a save that read `items` would race the setState it was queued behind.
   */
  const mutate = useCallback(
    (next: ModuleItem[]) => {
      setItems(next)
      void persist({items: next})
    },
    [persist],
  )

  /**
   * The title and the description, saved as they are typed.
   *
   * Debounced because a text field writes on every keystroke and a record write
   * per keystroke is a request per keystroke; the state is still updated every
   * time, so the field and the publish dialog never lag behind the typing.
   */
  const titleTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onTitle = useCallback(
    (value: string) => {
      setTitle(value)
      if (titleTimer.current) clearTimeout(titleTimer.current)
      titleTimer.current = setTimeout(() => void persist({title: value}), 700)
    },
    [persist],
  )
  const descriptionTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onDescription = useCallback(
    (value: string) => {
      setDescription(value)
      if (descriptionTimer.current) clearTimeout(descriptionTimer.current)
      descriptionTimer.current = setTimeout(
        () => void persist({description: value}),
        700,
      )
    },
    [persist],
  )
  useEffect(
    () => () => {
      if (titleTimer.current) clearTimeout(titleTimer.current)
      if (descriptionTimer.current) clearTimeout(descriptionTimer.current)
    },
    [],
  )

  /** Step one: the module gets a name, a subject, a level — and a record. */
  const onCreate = useCallback(async () => {
    if (!did || !title.trim() || !level || !subject) return
    setBusy(true)
    try {
      const now = new Date().toISOString()
      const res = await writeModuleRecord({
        agent,
        did,
        record: moduleRecordOf({
          title: title.trim(),
          description,
          status: 'draft',
          items: [],
          level,
          subject,
          createdAt: now,
          updatedAt: now,
        }),
      })
      setModuleUri(res.uri)
      setCreatedAt(now)
      syncedRef.current = `${now}|0`
      /*
       * Straight into choosing, with no stop in between.
       *
       * The record exists now, so there is nothing left to decide before the
       * playlist — and the builder's own first move is always the same one
       * (*fill the first place*). Stopping to make the creator find that button
       * would be a tap that buys nothing, so the picker opens itself. It is also
       * where the *other* way in lives: a creator with nothing to choose yet is
       * offered *write the first capsule* by the sheet's own empty state.
       */
      pickerControl.open()
    } catch (e) {
      console.error('Failed to start the module', e)
    } finally {
      setBusy(false)
    }
  }, [agent, description, did, level, pickerControl, subject, title])

  const openPicker = useCallback(() => pickerControl.open(), [pickerControl])

  /**
   * Writing a capsule *inside* the module.
   *
   * The module's address and its two codes travel with the studio: the codes so
   * the new capsule cannot be filed under a level the module does not hold, and
   * the address so publishing appends it to the playlist the creator is looking
   * at. Without a record to append to — the fixture — the studio is still the
   * right place to go; it simply has nothing to hand back to.
   */
  const writeCapsule = useCallback(() => {
    navigation.navigate('CapsuleStudio', {
      ...(moduleUri ? {moduleUri} : null),
      ...(level ? {moduleLevel: level} : null),
      ...(subject ? {moduleSubject: subject} : null),
    })
  }, [level, moduleUri, navigation, subject])

  const onPublished = useCallback(
    (published: PublishedModule) => {
      if (classroom && forClassroom) {
        const roomName = classroom.name
        addClassroomContent
          .mutateAsync({
            classroomId: classroom.id,
            kind: 'module',
            visibility: published.forClassroom ? 'members' : 'public',
            text: description,
            moduleTitle: title,
            moduleUri: published.moduleUri,
            moduleCid: published.moduleCid,
            postUri: published.postUri,
            postCid: published.postCid,
          })
          .then(() =>
            Toast.show(
              published.forClassroom
                ? l`Module is only in ${roomName}`
                : l`Module added to ${roomName}`,
            ),
          )
          .catch(error => showClassroomError(i18n, error))
      }
      /*
       * Back to where the module was begun: the room it was written for, or the
       * creator's own page when one of its tabs sent them here.
       */
      if ((classroom || draftUri) && navigation.canGoBack()) {
        navigation.goBack()
        return
      }
      if (navigation.canGoBack()) navigation.popToTop()
    },
    [
      addClassroomContent,
      classroom,
      description,
      draftUri,
      forClassroom,
      i18n,
      l,
      navigation,
      title,
    ],
  )

  /*
   * A draft that has not arrived yet is not an empty module.
   *
   * The two look identical for a moment — no title, no capsules, a list of things
   * to do — and showing the second while the first is true is a lie the creator
   * reads: "it has forgotten my work". So the wait is drawn as a wait, and the
   * builder only appears once the record behind it has been read.
   */
  const waitingForDraft = !!draftUri && !draftQuery.data && !draftQuery.isError
  const isNew = !moduleUri && !waitingForDraft
  const canCreate = !!title.trim() && !!level && !!subject && !busy

  /*
   * One place in the playlist, drawn once for both ways of moving it.
   *
   * The two ways are not two components: a place is the same place whether it is
   * dragged by a finger or stepped by a screen reader, and the only difference is
   * which control it is handed. That is `SavedFeeds`' arrangement (see the feed
   * list) and it is what keeps the numbers, the states and the fixes from having
   * to be written twice and drifting apart.
   */
  const renderSlot = (item: ModuleItem, dragHandle?: ReactNode) => {
    const slot = slotByUri.get(item.uri)
    const capsule = slot && slot.kind !== 'missing' ? slot.capsule : undefined
    const index = items.findIndex(candidate => candidate.uri === item.uri)
    const position = index + 1
    const testID = `moduleStudioSlot-${position}`
    return (
      <SlotRow
        key={item.uri}
        testID={testID}
        position={position}
        title={
          capsule
            ? capsule.title?.trim() || l`Untitled capsule`
            : l`Not available`
        }
        coins={capsule?.slideCount ?? 0}
        state={slot ? slot.kind : 'missing'}
        thumbnail={thumbnailFor(item)}
        dragHandle={dragHandle}
        canMoveUp={index > 0}
        canMoveDown={index < items.length - 1}
        onEarlier={() => mutate(moveModuleItem(items, item.uri, -1))}
        onLater={() => mutate(moveModuleItem(items, item.uri, 1))}
        onRemove={() => mutate(removeModuleItem(items, item.uri))}
        onFix={
          slot?.kind === 'draft'
            ? () => navigation.navigate('CapsuleStudio', {draftUri: item.uri})
            : slot?.kind === 'missing' || slot?.kind === 'mismatch'
              ? openPicker
              : undefined
        }
      />
    )
  }

  /**
   * Where this module is probably filed — asked once, answered in one tap.
   *
   * The first step used to be four taps before a creator could begin: pick the
   * school, pick the year, pick the subject, press *Start*. Three of those four
   * were not questions. A creator's work is already filed, and
   * `moduleFilingSuggestion` reads the filing straight off it — so the answers
   * arrive pre-selected, the step becomes *type a name and go*, and every chip
   * stays tappable for the module that really is for another year.
   *
   * The guess is made once and never re-applied: it fires when the step opens
   * on nothing, and a creator who has chosen something is not guessing.
   */
  useEffect(() => {
    if (fixture || !isNew || capsulesPending || filingGuessed) return
    if (category || level || subject) return
    const guess = moduleFilingSuggestion(capsules)
    if (!guess) return
    setFilingGuessed(true)
    setCategory(categoryOfLevel(guess.level))
    setLevel(guess.level)
    setSubject(guess.subject)
  }, [
    capsules,
    capsulesPending,
    category,
    filingGuessed,
    fixture,
    isNew,
    level,
    subject,
  ])

  if (waitingForDraft) {
    return (
      <Layout.Screen testID="moduleStudioScreen">
        <Layout.Header.Outer>
          <Layout.Header.BackButton />
          <Layout.Header.Content>
            <Layout.Header.TitleText>
              <Trans>Module</Trans>
            </Layout.Header.TitleText>
          </Layout.Header.Content>
          <Layout.Header.Slot />
        </Layout.Header.Outer>
        <Layout.Content>
          <View
            testID="moduleStudioLoading"
            style={[a.flex_row, a.align_center, a.gap_sm, a.p_lg]}>
            <Loader size="sm" />
            <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
              <Trans>Opening the module…</Trans>
            </Text>
          </View>
        </Layout.Content>
      </Layout.Screen>
    )
  }

  return (
    <Layout.Screen testID="moduleStudioScreen">
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>
            {isNew ? <Trans>New module</Trans> : <Trans>Module</Trans>}
          </Layout.Header.TitleText>
        </Layout.Header.Content>
        {/*
         * The slot stays empty, and the save state lives with the playlist.
         *
         * `Layout.Header.Slot` is a **33px** column — one round icon button wide
         * (`HEADER_SLOT_SIZE`) — so a word in it does not overflow, it *wraps*: the
         * first build of this screen drew "Save" with the "d" on a second line, half
         * outside the header. A header slot holds an action; this is a status, and
         * the status belongs beside the thing being saved (see the Playlist row).
         */}
        <Layout.Header.Slot />
      </Layout.Header.Outer>

      <Layout.Content
        ref={scrollRef}
        testID="moduleStudioContent"
        scrollEnabled={!isDragging}
        contentContainerStyle={[a.px_md, a.pb_4xl, a.gap_lg]}>
        {forClassroom && classroom ? (
          <View
            testID="moduleStudioClassroom"
            style={[styles.note, t.atoms.bg_contrast_25]}>
            <Text style={[a.text_sm, t.atoms.text]}>
              <Trans>
                This module is being built for {classroom.name}. You will be
                asked who can play it when you publish.
              </Trans>
            </Text>
          </View>
        ) : null}

        {isNew ? (
          <>
            <View style={[a.gap_xs]}>
              <Text style={[a.text_2xl, a.font_bold, t.atoms.text]}>
                <Trans>What is this module?</Trans>
              </Text>
              <Text
                style={[
                  a.text_sm,
                  a.leading_snug,
                  t.atoms.text_contrast_medium,
                ]}>
                <Trans>
                  A module is a playlist of your capsules: one subject, one
                  level, played in order as one run. Name it and file it, and
                  the six places it needs are waiting on the next screen.
                </Trans>
              </Text>
            </View>

            <TextField.Root>
              <TextField.Input
                testID="moduleStudioTitle"
                label={l`Module title`}
                placeholder={l`e.g. Fractions, the whole unit`}
                defaultValue={title}
                onChangeText={onTitle}
                /*
                 * The keyboard's *done* starts the module.
                 *
                 * With the filing pre-filled, naming it is the whole of the
                 * first step — so the last keystroke is the last tap, and the
                 * button below becomes the way out for someone who would rather
                 * look at what was guessed first. Both do the same thing.
                 */
                onSubmitEditing={() => {
                  if (canCreate) void onCreate()
                }}
                returnKeyType="done"
                autoFocus
                maxLength={120}
              />
            </TextField.Root>

            <TextField.Root>
              <TextField.Input
                testID="moduleStudioDescription"
                label={l`What is it about? (optional)`}
                placeholder={l`One or two lines for the module's page`}
                defaultValue={description}
                onChangeText={onDescription}
                multiline
                numberOfLines={3}
              />
            </TextField.Root>

            {roomLevel ? (
              /*
               * The room's answers, shown instead of the chips.
               *
               * The module is the room's course, so its school and year are the
               * room's — offering a choice here would only be offering to file
               * it somewhere the room does not hold it.
               */
              <View testID="moduleStudioRoomLevel" style={[a.gap_xs]}>
                <Text
                  style={[
                    a.text_xs,
                    a.font_semi_bold,
                    t.atoms.text_contrast_medium,
                  ]}>
                  {l`School`}
                </Text>
                <Text style={[a.text_sm]}>
                  {l`Built for ${classroom?.name ?? ''} at ${l(gradeLabel(roomLevel) ?? {message: roomLevel})}.`}
                </Text>
              </View>
            ) : (
              <>
                <ChipRow
                  label={l`School`}
                  testIDPrefix="moduleStudioCategory"
                  options={CAPSULE_CATEGORIES.map(option => ({
                    value: option.value,
                    label: l(option.label),
                  }))}
                  selected={category}
                  onSelect={value => {
                    setCategory(value)
                    // A year from another school is not a year in this one, so the
                    // choice is cleared rather than left to be silently wrong.
                    setLevel(null)
                    setSubject(null)
                  }}
                />

                {category ? (
                  <ChipRow
                    label={l`Year`}
                    testIDPrefix="moduleStudioLevel"
                    options={CAPSULE_GRADES[category].map(option => ({
                      value: option.value,
                      label: l(option.label),
                    }))}
                    selected={level}
                    onSelect={value => {
                      setLevel(value)
                      setSubject(null)
                    }}
                  />
                ) : null}
              </>
            )}

            {level ? (
              <ChipRow
                label={l`Subject`}
                testIDPrefix="moduleStudioSubject"
                options={getAvailableSubjects(level).map(option => ({
                  value: option.value,
                  label: l(option.label),
                }))}
                selected={subject}
                onSelect={setSubject}
              />
            ) : null}

            {level && subject ? (
              <View
                testID="moduleStudioScope"
                style={[styles.note, t.atoms.bg_contrast_25]}>
                <Text style={[a.text_sm, t.atoms.text]}>
                  {filingGuessed ? (
                    <Trans>
                      Filed the way your other capsules are — change it above if
                      this one is for another year.{' '}
                    </Trans>
                  ) : null}
                  <Trans>
                    Only {l(subjectLabel(subject) ?? {message: subject})}{' '}
                    capsules at {l(gradeLabel(level) ?? {message: level})} can
                    go in this module — a playlist that mixed years would not be
                    one course.
                  </Trans>
                </Text>
              </View>
            ) : null}

            <Button
              testID="moduleStudioCreate"
              label={l`Start the module`}
              size="large"
              color="primary"
              disabled={!canCreate}
              onPress={onCreate}>
              <ButtonText>
                <Trans>Start the module</Trans>
              </ButtonText>
              <ButtonIcon icon={busy ? Loader : ChevronRight} />
            </Button>
          </>
        ) : (
          <>
            {/* What the module is, and the way to change it. */}
            <View style={[a.gap_sm]}>
              <View style={[a.flex_row, a.align_start, a.gap_sm]}>
                <Text
                  testID="moduleStudioHeading"
                  style={[a.flex_1, a.text_2xl, a.font_bold, t.atoms.text]}>
                  {title || l`Untitled module`}
                </Text>
                <Button
                  testID="moduleStudioDetailsToggle"
                  label={
                    detailsOpen ? l`Hide the details` : l`Edit the details`
                  }
                  size="tiny"
                  color="secondary"
                  onPress={() => setDetailsOpen(open => !open)}>
                  <ButtonIcon icon={Pencil} />
                </Button>
              </View>
              <View style={[a.flex_row, a.align_center, a.flex_wrap, a.gap_xs]}>
                {subject ? (
                  <Chip
                    label={l(subjectLabel(subject) ?? {message: subject})}
                  />
                ) : null}
                {level ? (
                  <Chip label={l(gradeLabel(level) ?? {message: level})} />
                ) : null}
                <Chip
                  label={l`${ready} of ${Math.max(MODULE_MIN_CAPSULES, items.length)} published`}
                />
                {coins > 0 ? (
                  <Chip
                    label={l`${coins} ${coins === 1 ? 'coin' : 'coins'} to win`}
                  />
                ) : null}
              </View>
            </View>

            {detailsOpen ? (
              <View style={[a.gap_md, styles.note, t.atoms.bg_contrast_25]}>
                <TextField.Root>
                  <TextField.Input
                    testID="moduleStudioTitle"
                    label={l`Module title`}
                    defaultValue={title}
                    onChangeText={onTitle}
                    maxLength={120}
                  />
                </TextField.Root>
                <TextField.Root>
                  <TextField.Input
                    testID="moduleStudioDescription"
                    label={l`What is it about? (optional)`}
                    defaultValue={description}
                    onChangeText={onDescription}
                    multiline
                    numberOfLines={3}
                  />
                </TextField.Root>
                <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                  <Trans>
                    The subject and the level are what the playlist is: change
                    them and its capsules no longer belong in it, so they are
                    settled when the module is created.
                  </Trans>
                </Text>
              </View>
            ) : null}

            {/* The six places. */}
            <View style={[a.gap_sm]}>
              <View style={[a.flex_row, a.align_center, a.gap_sm]}>
                <View style={[a.flex_1, a.flex_row, a.align_center, a.gap_xs]}>
                  <Text style={[a.text_lg, a.font_bold, t.atoms.text]}>
                    <Trans>Playlist</Trans>
                  </Text>
                  {saving ? <Loader size="xs" /> : null}
                  {saving || moduleUri ? (
                    <Text
                      testID={
                        saving ? 'moduleStudioSaving' : 'moduleStudioSaved'
                      }
                      style={[a.text_xs, t.atoms.text_contrast_medium]}>
                      {' · '}
                      {saving ? <Trans>Saving…</Trans> : <Trans>Saved</Trans>}
                    </Text>
                  ) : null}
                </View>
                <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                  {items.length >= MODULE_MIN_CAPSULES ? (
                    <Plural
                      value={items.length}
                      one="# capsule"
                      other="# capsules"
                    />
                  ) : (
                    // What is still missing, including on an empty playlist.
                    <Plural
                      value={MODULE_MIN_CAPSULES - items.length}
                      one="# more capsule needed to publish"
                      other="# more capsules needed to publish"
                    />
                  )}
                </Text>
              </View>

              {capsulesPending && items.length === 0 ? (
                <View style={[a.flex_row, a.align_center, a.gap_sm, a.py_lg]}>
                  <Loader size="sm" />
                  <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                    <Trans>Looking through your capsules…</Trans>
                  </Text>
                </View>
              ) : (
                <View testID="moduleStudioSlots">
                  {/*
                    The playlist, and the way its order is changed.

                    Six places are dragged into order rather than stepped into
                    it. The build before this drew three buttons on every row
                    (*earlier*, *later*, *remove*) and moving the sixth capsule
                    to the top took five deliberate taps on a 33px target; a
                    drag does it in one movement, which is the whole reason the
                    list is a list. `SortableList` is the app's own drag list —
                    the one `SavedFeeds` reorders pinned feeds with — so a
                    creator who has ordered their feeds already knows this
                    gesture, and the only control left on the row is the one that
                    takes a place out.

                    Screen readers get the two-step version instead, and that is
                    not a fallback bolted on: a drag is invisible to somebody
                    who is not looking at it, so `SavedFeeds` keeps up/down
                    buttons for exactly this case and this list does the same.
                    Both paths draw the same row through `renderSlot`, so the
                    numbers, the states and the fixes cannot drift apart.
                  */}
                  {screenReaderEnabled ? (
                    items.map(item => renderSlot(item))
                  ) : (
                    <SortableList
                      data={items}
                      keyExtractor={moduleItemKey}
                      itemHeight={MODULE_ROW_STRIDE}
                      scrollRef={scrollRef}
                      scrollOffset={scrollOffset}
                      onDragStart={() => setIsDragging(true)}
                      onDragEnd={() => setIsDragging(false)}
                      onReorder={mutate}
                      renderItem={(item, dragHandle) =>
                        renderSlot(item, dragHandle)
                      }
                    />
                  )}

                  {/*
                    The way in — one place, not six.

                    It used to draw every place that does not exist yet, each with
                    its own small "Add a capsule": nine controls for two ideas, and
                    a creator who had not written a capsule yet met the shape of an
                    obligation before they met the shape of a module. What is drawn
                    now is the *next* place (there is only ever one place you can
                    fill next), and when the playlist is still empty, one
                    invitation instead of it — see `EmptyModuleInvitation`.
                  */}
                  {items.length === 0 ? (
                    <EmptyModuleInvitation
                      testID="moduleStudioEmpty-1"
                      count={MODULE_MIN_CAPSULES}
                      onChoose={openPicker}
                      onCreate={writeCapsule}
                    />
                  ) : (
                    <EmptySlot
                      testID={
                        items.length >= MODULE_MIN_CAPSULES
                          ? 'moduleStudioEmpty-extra'
                          : `moduleStudioEmpty-${items.length + 1}`
                      }
                      position={items.length + 1}
                      onChoose={openPicker}
                      onCreate={writeCapsule}
                    />
                  )}
                </View>
              )}

              {/*
                There is no separate *Choose capsules / Write a capsule* pair any
                more. The next place — or, on an empty module, the invitation —
                already offers both actions at full size and in the place the eye
                already is, so the pair was two more buttons for the same two ideas
                (and, on an empty playlist, a third and fourth copy of them).
              */}
            </View>

            {/* What is left to do before it can go out. */}
            {blockers.length > 0 ? (
              <View
                testID="moduleStudioBlockers"
                style={[styles.note, a.gap_xs, t.atoms.bg_contrast_25]}>
                <Text style={[a.text_sm, a.font_bold, t.atoms.text]}>
                  <Trans>Before this module can be published</Trans>
                </Text>
                {blockers.map(blocker => (
                  <Text
                    key={`${blocker.code}-${'uri' in blocker ? blocker.uri : ''}`}
                    style={[a.text_sm, t.atoms.text_contrast_medium]}>
                    • {moduleBlockerSentence(blocker, l)}
                  </Text>
                ))}
              </View>
            ) : (
              <View
                testID="moduleStudioReady"
                style={[
                  styles.note,
                  a.flex_row,
                  a.align_center,
                  a.gap_sm,
                  t.atoms.bg_contrast_25,
                ]}>
                <CircleCheck
                  size="sm"
                  style={[{color: t.palette.positive_500}]}
                />
                <Text style={[a.flex_1, a.text_sm, t.atoms.text]}>
                  <Trans>
                    Every capsule here is published, and they all belong to this
                    subject and level. The module is ready to go out.
                  </Trans>
                </Text>
              </View>
            )}

            <Button
              testID="moduleStudioPublish"
              label={l`Publish module`}
              size="large"
              color="primary"
              disabled={blockers.length > 0}
              onPress={() => publishControl.open()}>
              <ButtonText>
                {forClassroom
                  ? l`Publish into the classroom`
                  : l`Publish module`}
              </ButtonText>
            </Button>
          </>
        )}
      </Layout.Content>

      {/*
        The picker, over the builder.
        
        `taken` is the playlist as it stands, so a capsule already in the module
        is never offered twice — and the places that are still empty are what the
        `alreadyPlaced` count is for: the sheet says how many more are needed to
        publish, which is the one question a creator has while choosing.
      */}
      <ModuleCapsulePicker
        control={pickerControl}
        level={level}
        subject={subject}
        taken={items.map(item => item.uri)}
        alreadyPlaced={items.length}
        onAdd={chosen =>
          mutate(chosen.reduce<ModuleItem[]>(appendModuleItem, items))
        }
        onCreate={writeCapsule}
      />

      <PublishModuleDialog
        control={publishControl}
        moduleUri={moduleUri}
        createdAt={createdAt}
        title={title}
        description={description}
        level={level}
        subject={subject}
        items={items}
        coins={coins}
        blockers={blockers}
        classroomName={forClassroom ? classroom?.name : undefined}
        onSuccess={onPublished}
      />
    </Layout.Screen>
  )
}

/**
 * One place in the playlist, filled.
 *
 * A row rather than a tile, and that is the decision this screen turns on. The
 * *card* in a feed is a carousel because a reader is meeting the module; this is
 * the person assembling it, whose four questions are all about order and state —
 * *what is third*, *how do I make it second*, *which of these still needs writing*,
 * *take that out*. A row answers all four in the space of one line, and it does so
 * on a phone as well as on a laptop, which a grid of tiles does not.
 *
 * Every state is named on the row and none of them are hidden: a draft says
 * *Draft* and offers the way to finish it, a capsule that cannot be read says so
 * and offers the way to replace it, and a capsule filed under another level says
 * that, because a creator whose capsule moved year needs to know why it is not
 * publishable. Nothing about the row is inferable only from its colour.
 */
function SlotRow({
  testID,
  position,
  title,
  coins,
  state,
  thumbnail,
  dragHandle,
  canMoveUp,
  canMoveDown,
  onEarlier,
  onLater,
  onRemove,
  onFix,
}: {
  testID: string
  position: number
  title: string
  coins: number
  state: 'filled' | 'draft' | 'missing' | 'mismatch'
  thumbnail?: string
  /**
   * The grip, when the playlist is dragged — and the row's own tell for which
   * list it is in.
   *
   * A row handed a grip ends with *remove* alone; a row handed none (a screen
   * reader, which cannot drag) ends with *up*, *down* and *remove*. The row does
   * not ask which list it is in; it only knows what it was handed, which is what
   * keeps one row rendering the same facts in both.
   */
  dragHandle?: ReactNode
  /** Nothing above it to swap with, so *move up* is a no-op worth saying. */
  canMoveUp: boolean
  /** Nothing below it to swap with. */
  canMoveDown: boolean
  onEarlier: () => void
  onLater: () => void
  onRemove: () => void
  /** Finishing a draft, or replacing a place that cannot be read. */
  onFix?: () => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()

  return (
    <View
      testID={testID}
      style={[
        styles.slot,
        t.atoms.bg_contrast_25,
        t.atoms.border_contrast_low,
      ]}>
      {dragHandle}
      <View
        style={[
          styles.number,
          state === 'filled'
            ? {backgroundColor: t.palette.primary_500}
            : t.atoms.bg_contrast_100,
        ]}>
        <Text
          style={[
            a.text_xs,
            a.font_bold,
            state === 'filled'
              ? {color: t.palette.white}
              : t.atoms.text_contrast_medium,
          ]}>
          {position}
        </Text>
      </View>

      <View style={[styles.poster, t.atoms.bg_contrast_50]}>
        {thumbnail ? (
          <Image
            source={{uri: thumbnail}}
            style={StyleSheet.absoluteFill}
            contentFit="cover"
            accessibilityIgnoresInvertColors
          />
        ) : (
          <Text style={[a.text_lg, t.atoms.text_contrast_low]}>
            {state === 'filled' ? '▶' : '·'}
          </Text>
        )}
      </View>

      <View style={[a.flex_1, a.gap_xs]}>
        {/*
          One line, because the drag needs every row to be the same height —
          see `MODULE_ROW_HEIGHT`. A title that wrapped would be a title that
          moved the row underneath the finger holding it.
        */}
        <Text
          numberOfLines={1}
          style={[a.text_sm, a.font_semi_bold, t.atoms.text]}>
          {title}
        </Text>
        <View style={[a.flex_row, a.align_center, a.flex_wrap, a.gap_xs]}>
          {state === 'draft' ? (
            <StateChip label={l`Draft`} tone="warn" />
          ) : null}
          {state === 'missing' ? (
            <StateChip label={l`Not available`} tone="warn" />
          ) : null}
          {state === 'mismatch' ? (
            <StateChip label={l`Another level`} tone="warn" />
          ) : null}
          {coins > 0 ? (
            <StateChip
              label={l`${coins} ${coins === 1 ? 'coin' : 'coins'}`}
              tone="plain"
            />
          ) : null}
        </View>
      </View>

      <View style={[a.flex_row, a.align_center, a.gap_xs]}>
        {onFix ? (
          <Button
            testID={`${testID}-fix`}
            label={
              state === 'draft' ? l`Finish this capsule` : l`Replace this place`
            }
            size="tiny"
            color="primary"
            onPress={onFix}>
            <ButtonText>
              {state === 'draft' ? (
                <Trans>Finish</Trans>
              ) : (
                <Trans>Replace</Trans>
              )}
            </ButtonText>
          </Button>
        ) : null}
        {/*
          *Up* and *down*, and only for a screen reader.

          The list is drawn top to bottom, so the control moves the row the way
          the row is drawn — these were left and right chevrons once, which is
          the one pair of arrows that cannot mean anything in a column: nobody
          has to translate a `<` into *earlier* while a drag is unavailable to
          them. Sighted fingers get the grip at the head of the row instead.

          The two ends are *disabled* rather than silently doing nothing: on the
          first capsule there is nothing to swap with, and a button that swallows
          a tap is how somebody concludes the app is broken.
        */}
        {dragHandle ? null : (
          <>
            <IconButton
              testID={`${testID}-earlier`}
              label={l`Move it up in the playlist`}
              icon={ArrowTop}
              disabled={!canMoveUp}
              onPress={onEarlier}
            />
            <IconButton
              testID={`${testID}-later`}
              label={l`Move it down in the playlist`}
              icon={ArrowBottom}
              disabled={!canMoveDown}
              onPress={onLater}
            />
          </>
        )}
        <IconButton
          testID={`${testID}-remove`}
          label={l`Take it out of the playlist`}
          icon={Trash}
          onPress={onRemove}
        />
      </View>
    </View>
  )
}

/**
 * One place in the playlist, empty — and there is only ever one.
 *
 * The builder used to draw every place that did not exist yet as its own row with
 * its own small "Add a capsule": a creator three capsules into a six-capsule
 * module was looking at three buttons that all did exactly the same thing. Only
 * one place can be filled next, so only one place is drawn.
 *
 * Both ways in are here, at full size and not behind a menu: choose from the
 * library (the picker is multi-select, so as many as you like in one visit) or
 * write one now. The second is the flow the whole feature exists for — a creator
 * with nothing at this level writes their course from these places — so neither
 * action is hidden behind the other.
 */
function EmptySlot({
  testID,
  position,
  onChoose,
  onCreate,
}: {
  testID: string
  position: number
  onChoose: () => void
  onCreate: () => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()

  return (
    <View
      testID={testID}
      style={[
        styles.emptySlot,
        {borderColor: t.atoms.border_contrast_low.borderColor},
      ]}>
      <View style={[styles.number, t.atoms.bg_contrast_50]}>
        <Text style={[a.text_xs, a.font_bold, t.atoms.text_contrast_medium]}>
          {position}
        </Text>
      </View>
      <Button
        testID="moduleStudioAdd"
        label={l`Choose capsules from your library`}
        color="primary"
        size="large"
        style={[a.flex_1]}
        onPress={onChoose}>
        <ButtonIcon icon={Plus} />
        <ButtonText>
          <Trans>Add a capsule</Trans>
        </ButtonText>
      </Button>
      <Button
        testID="moduleStudioWrite"
        label={l`Write a new capsule in this module`}
        color="secondary"
        size="large"
        onPress={onCreate}>
        <ButtonText>
          <Trans>Write one</Trans>
        </ButtonText>
      </Button>
    </View>
  )
}

/**
 * An empty module, as one invitation.
 *
 * This is the first thing a creator sees after naming a module, and the first
 * build met them with six dashed rows, six small "Add a capsule" buttons, two
 * more buttons underneath, and a disabled *Publish* — nine controls and a dead end
 * for the two things that could actually be done. What an empty module owes
 * somebody is the *idea*, and one obvious way in:
 *
 *  - **the shape, as a shape.** One pip per capsule says *six capsules* without
 *    saying *you owe six buttons*. The count is the module's own constant.
 *  - **one sentence about what a module is**, because the step before this asked
 *    for a title and two codes and nobody has yet been told what they are
 *    assembling.
 *  - **both ways in, at full size.** *Choose capsules* opens the picker — which is
 *    multi-select, so filling all six places is one visit rather than six — and
 *    *Write a capsule* is the door for a creator who has nothing to choose yet.
 *    The same two actions, and the same two `testID`s, as the single empty place
 *    in a playlist that has already been started.
 */
function EmptyModuleInvitation({
  testID,
  count,
  onChoose,
  onCreate,
}: {
  testID: string
  count: number
  onChoose: () => void
  onCreate: () => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()

  return (
    <View
      testID={testID}
      style={[
        styles.invitation,
        t.atoms.bg_contrast_25,
        t.atoms.border_contrast_low,
      ]}>
      <View
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
        style={[a.flex_row, a.gap_xs]}>
        {Array.from({length: count}).map((_, index) => (
          <View key={index} style={[styles.pip, t.atoms.bg_contrast_100]} />
        ))}
      </View>

      <Text style={[a.text_lg, a.font_bold, t.atoms.text]}>
        <Plural
          value={count}
          one="A module is # capsule, played in order."
          other="A module is # capsules, played in order."
        />
      </Text>
      <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
        <Trans>
          Pick them from your library — as many at once as you like — or write
          the first one here. Each one is an ordinary capsule: already
          published, or saved to finish later.
        </Trans>
      </Text>

      <Button
        testID="moduleStudioAdd"
        label={l`Choose capsules from your library`}
        color="primary"
        size="large"
        onPress={onChoose}>
        <ButtonIcon icon={Plus} />
        <ButtonText>
          <Trans>Choose capsules</Trans>
        </ButtonText>
      </Button>
      <Button
        testID="moduleStudioWrite"
        label={l`Write a new capsule in this module`}
        color="secondary"
        size="large"
        onPress={onCreate}>
        <ButtonIcon icon={Pencil} />
        <ButtonText>
          <Trans>Write a capsule</Trans>
        </ButtonText>
      </Button>
    </View>
  )
}

/**
 * The square icon button a place's edits use.
 *
 * `size="tiny"` renders 25×25 — measured, and under every touch target rule
 * there is (iOS asks 44, Android 48, and a thumb on a phone does not hit 25
 * twice in a row). `small` is the app's own compact icon button at 33×33, which
 * is what the rest of Taderok uses beside a list, and it is the size this screen
 * was the outlier on.
 */
function IconButton({
  testID,
  label,
  icon,
  disabled,
  onPress,
}: {
  testID: string
  label: string
  icon: ComponentProps<typeof ButtonIcon>['icon']
  disabled?: boolean
  onPress: () => void
}) {
  return (
    <Button
      testID={testID}
      label={label}
      size="small"
      color="secondary"
      shape="square"
      disabled={disabled}
      onPress={onPress}>
      <ButtonIcon icon={icon} />
    </Button>
  )
}

/**
 * One filter, as a strip of chips.
 *
 * A strip rather than a wrapped block, for the reason the classroom's picker
 * gives: two lists of chips that wrap are taller than the thing they are choosing
 * for. The difference here is the copy — the label is never "All", because a
 * module is *one* subject at one level and "all" is not an answer it can hold.
 */
function ChipRow({
  label,
  testIDPrefix,
  options,
  selected,
  onSelect,
}: {
  label: string
  testIDPrefix: string
  options: {value: string; label: string}[]
  selected: string | null
  onSelect: (value: string) => void
}) {
  const t = useTheme()

  return (
    <View style={[a.gap_xs]}>
      <Text style={[a.text_xs, a.font_semi_bold, t.atoms.text_contrast_medium]}>
        {label}
      </Text>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={[a.flex_row, a.gap_xs]}>
        {options.map(option => (
          <Button
            key={option.value}
            testID={`${testIDPrefix}-${option.value}`}
            label={option.label}
            size="small"
            color={selected === option.value ? 'primary' : 'secondary'}
            shape="default"
            onPress={() => onSelect(option.value)}>
            <ButtonText>{option.label}</ButtonText>
          </Button>
        ))}
      </ScrollView>
    </View>
  )
}

/** A read-only fact about the module, in the header of the playlist. */
function Chip({label}: {label: string}) {
  const t = useTheme()
  return (
    <View style={[styles.chip, t.atoms.bg_contrast_50]}>
      <Text style={[a.text_xs, a.font_semi_bold, t.atoms.text]}>{label}</Text>
    </View>
  )
}

/**
 * A state, on a place.
 *
 * Two tones and no more: `warn` is *this one needs doing something about* — a
 * draft, a place that cannot be read, a capsule filed elsewhere — and `plain` is
 * a fact. Colour is the second signal here and never the first: the words are on
 * the chip.
 */
function StateChip({label, tone}: {label: string; tone: 'warn' | 'plain'}) {
  const t = useTheme()
  return (
    <View
      style={[
        styles.chip,
        tone === 'warn'
          ? {backgroundColor: t.atoms.bg_contrast_50.backgroundColor}
          : t.atoms.bg_contrast_25,
      ]}>
      <Text style={[a.text_xs, a.font_semi_bold, t.atoms.text]}>{label}</Text>
    </View>
  )
}

const styles = StyleSheet.create({
  chip: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
  },
  note: {
    padding: 12,
    borderRadius: 10,
  },
  slot: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 8,
    height: MODULE_ROW_HEIGHT,
    /** The gap is the row's own, so a dragged row carries it. */
    marginBottom: MODULE_ROW_GAP,
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
  },
  emptySlot: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 8,
    borderRadius: 12,
    borderWidth: 1,
    borderStyle: 'dashed',
  },
  invitation: {
    gap: 12,
    padding: 14,
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
  },
  /** One place in the module, as a mark rather than as a button. */
  pip: {
    flex: 1,
    height: 6,
    borderRadius: 3,
  },
  number: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  poster: {
    width: 40,
    aspectRatio: 4 / 5,
    borderRadius: 8,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
})
